import React from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { getCategoryColor, getCategoryIcon } from '../../constants/Categories';
import type { AssetSystemLayer } from '../../viewers/autodesk/assetMarkerGrouping';
import { UNASSIGNED_ASSET_LAYER } from '../../viewers/autodesk/assetMarkerGrouping';

/**
 * LayersPanel — THE layers section, shared by every surface that has one.
 *
 * WHY THIS EXISTS. Commit 4a568dc ("one Browser pane in every viewer") unified
 * <POIHierarchyBrowser> across the RVT viewer, the IFC viewer and the App-level panorama and 2D
 * panes. The LAYERS block above it was left out of that unification: AutodeskViewer kept its own
 * copy and the panorama pane kept another. The split then bit exactly as you would expect — asset
 * layers were added to the viewer's copy, their flags rendered on the panorama too, and the
 * panorama's own pane had no way to turn them off.
 *
 * So this is presentation only. It owns NO state: every toggle is a callback, and the selection
 * lives in App so both panes move the same flags. A surface that lacks something (no panoramas,
 * no assets) simply passes nothing and that group disappears — the same self-gating convention
 * POIHierarchyBrowser uses for its capability callbacks.
 */

export interface LayersPanelProps {
  /** Panorama layer. Omit onTogglePanoramas to hide the row entirely. */
  panoramaCount?: number;
  showPanoramaLayer?: boolean;
  onTogglePanoramas?: (visible: boolean) => void;

  /** Asset layers, grouped by system. */
  assetSystemLayers?: AssetSystemLayer[];
  enabledAssetSystems?: Set<string>;
  onToggleAssetSystem?: (systemId: string) => void;
  onToggleAllAssetSystems?: () => void;
  isAssetsOpen?: boolean;
  onToggleAssetsOpen?: () => void;

  /** 2D plans projected onto the model (source request §5). */
  planCount?: number;
  showPlans?: boolean;
  onTogglePlans?: (visible: boolean) => void;
  onOpenPlans?: () => void;

  /** Planning objects (source request §7). Omit the callback to hide the row entirely. */
  planningObjectCount?: number;
  showPlanningObjects?: boolean;
  onTogglePlanningObjects?: (visible: boolean) => void;

  isRTL?: boolean;
  /** Heading is shown by default; a host that supplies its own can turn it off. */
  showHeading?: boolean;
}

const rowStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 8px',
  borderRadius: '4px',
  backgroundColor: active ? 'var(--dt-accent-tint)' : 'transparent',
  cursor: 'pointer',
});

export const LayersPanel: React.FC<LayersPanelProps> = ({
  panoramaCount = 0,
  showPanoramaLayer = false,
  onTogglePanoramas,
  assetSystemLayers = [],
  enabledAssetSystems,
  onToggleAssetSystem,
  onToggleAllAssetSystems,
  isAssetsOpen = false,
  onToggleAssetsOpen,
  planCount = 0,
  showPlans = false,
  onTogglePlans,
  onOpenPlans,
  planningObjectCount = 0,
  showPlanningObjects = false,
  onTogglePlanningObjects,
  isRTL = false,
  showHeading = true,
}) => {
  const { t } = useTranslation();
  const enabled = enabledAssetSystems ?? new Set<string>();
  const hasPanoramas = panoramaCount > 0 && !!onTogglePanoramas;
  const hasAssets = assetSystemLayers.length > 0 && !!onToggleAssetSystem;
  // Creation remains available in the Planning workspace; layers describe existing content.
  const hasPlanningObjects = planningObjectCount > 0 && !!onTogglePlanningObjects;
  const hasPlans = planCount > 0 && !!onTogglePlans;
  if (!hasPanoramas && !hasAssets && !hasPlanningObjects && !hasPlans) return null;

  const assetTotal = assetSystemLayers.every(layer => layer.assetKeys)
    ? new Set(assetSystemLayers.flatMap(layer => layer.assetKeys!)).size
    : assetSystemLayers.reduce((sum, layer) => sum + layer.count, 0);
  const allAssetsOn = assetSystemLayers.length > 0
    && assetSystemLayers.every((layer) => enabled.has(layer.id));
  const someAssetsOn = assetSystemLayers.some((layer) => enabled.has(layer.id));

  return (
    <div style={{ marginBottom: '16px' }}>
      {showHeading && (
        <div style={{
          color: 'var(--dt-text-3)',
          fontSize: '12px',
          marginBottom: '8px',
          fontWeight: 500,
          textTransform: 'uppercase',
        }}>
          Layers
        </div>
      )}

      {hasPanoramas && (
        <label style={rowStyle(showPanoramaLayer)}>
          <input
            type="checkbox"
            checked={showPanoramaLayer}
            onChange={(event) => onTogglePanoramas?.(event.target.checked)}
            style={{ cursor: 'pointer', accentColor: 'var(--dt-accent)' }}
          />
          <span style={{ flex: 1, color: 'var(--dt-text)', fontSize: '12px' }}>
            🌐 Panoramas ({panoramaCount})
          </span>
        </label>
      )}

      {hasPlans && (
        <div style={rowStyle(showPlans)}>
          <input type="checkbox" checked={showPlans} onChange={event => onTogglePlans?.(event.target.checked)}
            aria-label={t('planningPlansVisibility')} style={{ accentColor: 'var(--dt-accent)' }} />
          {onOpenPlans ? <button type="button" className="dt-planning-layer-link" onClick={onOpenPlans}
            title={t('planningOpenPlans')}>{t('sitePlansTitle')}{planCount > 0 ? ` (${planCount})` : ''}</button>
            : <span>{t('sitePlansTitle')}{planCount > 0 ? ` (${planCount})` : ''}</span>}
        </div>
      )}
      {hasPlanningObjects && (
        <label style={rowStyle(showPlanningObjects)}>
          <input type="checkbox" checked={showPlanningObjects}
            onChange={event => onTogglePlanningObjects?.(event.target.checked)}
            style={{ accentColor: 'var(--dt-accent)' }} />
          <span className="dt-planning-layer-name">{t('planningMyObjects')}{planningObjectCount > 0 ? ` (${planningObjectCount})` : ''}</span>
        </label>
      )}

      {hasAssets && (
        <>
          <div style={{ ...rowStyle(someAssetsOn), cursor: 'default' }}>
            <input
              type="checkbox"
              checked={allAssetsOn}
              // Mixed selection reads as a dash — otherwise a group with some systems on looks
              // identical to one with none.
              ref={(element) => {
                if (element) element.indeterminate = someAssetsOn && !allAssetsOn;
              }}
              onChange={() => onToggleAllAssetSystems?.()}
              aria-label="Show all asset systems"
              style={{ cursor: 'pointer', accentColor: 'var(--dt-accent)' }}
            />
            <button
              type="button"
              onClick={() => onToggleAssetsOpen?.()}
              aria-expanded={isAssetsOpen}
              title={isAssetsOpen ? 'Collapse assets' : 'Expand assets'}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                color: 'var(--dt-text)',
                fontSize: '12px',
                textAlign: isRTL ? 'right' : 'left',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  transition: 'transform 0.15s ease',
                  transform: isAssetsOpen ? 'rotate(90deg)' : 'none',
                  color: 'var(--dt-text-3)',
                }}
              >
                ▶
              </span>
              <span style={{ flex: 1 }}>📦 Assets ({assetTotal})</span>
            </button>
          </div>

          {isAssetsOpen && assetSystemLayers.map((layer) => {
            const on = enabled.has(layer.id);
            return (
              <label
                key={layer.id}
                style={{ ...rowStyle(on), marginInlineStart: '18px' }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => onToggleAssetSystem?.(layer.id)}
                  style={{ cursor: 'pointer', accentColor: 'var(--dt-accent)' }}
                />
                <span
                  aria-hidden="true"
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: getCategoryColor(layer.name),
                  }}
                />
                <span style={{ flex: 1, color: 'var(--dt-text)', fontSize: '12px' }}>
                  {getCategoryIcon(layer.name)} {layer.id === UNASSIGNED_ASSET_LAYER ? t('dt_assets_unassignedSystem') : layer.name} ({layer.count})
                </span>
              </label>
            );
          })}
        </>
      )}
    </div>
  );
};

export default LayersPanel;
